import {
  intervalToDuration,
  isBefore,
  isEqual,
  parseISO,
  startOfDay,
} from 'date-fns';

import {
  AMOUNT_PATTERN,
  AMOUNT_WITHOUT_DECIMAL_PATTERN,
} from '@/constants/patterns';

import { Tenant } from '@/types/common';
import { CountryISO } from '@/types/places';
import { AddressDetails } from '@/types/user';

import { isCaymanTenant } from './tenant';

export const isAboveYearsOld = (years: number, birthday?: string | number) => {
  if (!birthday) return false;

  try {
    const duration = intervalToDuration({
      start: new Date(birthday),
      end: new Date(),
    });
    if (duration.years && duration.years >= years) return true;
    return false;
  } catch {
    return false;
  }
};

export function isAbove18(birthday?: string | number) {
  return isAboveYearsOld(18, birthday);
}

export function isFutureDate(value: string) {
  if (!value) return false;

  const date = new Date(value as string);
  const today = new Date();

  if (isBefore(today, date)) {
    return true;
  }

  return false;
}

export function isOnOrBeforeDate(value: string, compareTo: string) {
  if (!value || !compareTo) return false;

  const date = startOfDay(parseISO(value));
  const compareDate = startOfDay(parseISO(compareTo));

  if (isBefore(date, compareDate) || isEqual(date, compareDate)) {
    return true;
  }

  return false;
}

export function isPastDate(value: string) {
  if (!value) return false;

  const date = startOfDay(parseISO(value));
  const today = startOfDay(new Date());

  return isBefore(date, today);
}

export function isCaymanAddress(userAddress?: AddressDetails) {
  return userAddress?.country === CountryISO.Cayman;
}

const AMOUNT_TRANSLATION_KEYS = {
  pattern: 'questionDrawer.monthlyDeposit.monthlyDeposit.pattern',
  caymanPattern:
    'questionDrawer.monthlyDeposit.monthlyDepositAmount.cayman.pattern',
} as const;

export const isValidAmount = (
  value: string,
  tenant: Tenant | undefined,
): string | true => {
  const isCayman = isCaymanTenant(tenant);

  if (!AMOUNT_PATTERN.test(value)) {
    return AMOUNT_TRANSLATION_KEYS.pattern;
  }

  if (isCayman && !AMOUNT_WITHOUT_DECIMAL_PATTERN.test(value)) {
    return AMOUNT_TRANSLATION_KEYS.caymanPattern;
  }

  return true;
};
